import { ReactElement, useCallback, useMemo } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useEventCallback } from '@mui/material';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import SipoPersonForm from 'shell/components/shared/sipo-person-form';
import PmiTitle from 'shell/components/shared/title';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { SearchPersonCivilUnionNonResidentCrumbs } from 'mutation/constants/registration-civil-union-non-resident-crumbs';
import { RegistrationCivilUnionFormVM } from 'mutation/view-models/registration-civil-union';

interface EditPersonsPageCivilUnionNonResidentSearchProps {
  goBackUrl?: string;
  redirectUrl?: string;
  breadcrumbItems?: BreadcrumbItem[];
  initialValues?: RegistrationCivilUnionFormVM;
}
const EditPersonsPageCivilUnionNonResidentSearchPage = ({
  breadcrumbItems = SearchPersonCivilUnionNonResidentCrumbs,
  redirectUrl = '/mutazioni/persona/unione-civile/non-residenti/registrazione/:idIntestatario/:idSchedaSoggettoANPR',
  goBackUrl = '/mutazioni/persona/unione-civile/non-residenti',
  initialValues = {},
}: EditPersonsPageCivilUnionNonResidentSearchProps): ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  // id intestatrio se presente fare get perchè siamo in modifica
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';
  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const isEdit = useMemo(() => {
    if ((idIntestatario && idIntestatario !== '0') || (idAnpr && idAnpr !== '0')) {
      return true;
    }

    return false;
  }, [idIntestatario, idAnpr]);

  const handleOnSuccess = useEventCallback((newIdIntestatario?: string) => {
    if (isEdit) {
      navigate({
        pathname: generatePath(redirectUrl, {
          idIntestatario: newIdIntestatario || idIntestatario || '0',
          idSchedaSoggettoANPR: idAnpr || '0',
        }),
      });

      return;
    }

    navigate({
      pathname: generatePath(redirectUrl, {
        idIntestatario: newIdIntestatario || '0',
        idSchedaSoggettoANPR: idAnpr || '0',
      }),
    });
  });

  const handleExit = useCallback(() => {
    navigate(goBackUrl);
  }, [goBackUrl]);

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbItems,
          {
            name: isEdit ? 'Modifica persona' : 'Nuova persona',
          },
        ]}
      />
      <PmiTitle title={isEdit ? 'Modifica persona' : 'Nuova persona'} />
      <SipoPersonForm
        onExit={handleExit}
        onSuccess={handleOnSuccess}
        urlGetData={
          idIntestatario && idIntestatario !== '0'
            ? `${__PERSON_API_URL__}/non-residenti/sipo/soggetto/${idIntestatario}`
            : `${__PERSON_NEW_API_URL__}/persona`
        }
        fiscalCode={fiscalCode}
        urlRegistration={`${__PERSON_API_URL__}/non-residenti/registrazione`}
        urlUpdate={`${__PERSON_API_URL__}/non-residenti/modifica`}
        initialValues={{
          generality: initialValues?.civilUnion?.generality,
        }}
        skipBirthActPartCheck
      />
    </>
  );
};

EditPersonsPageCivilUnionNonResidentSearchPage.displayName =
  'Person Sipo Edit Page Civil Union component';

export default EditPersonsPageCivilUnionNonResidentSearchPage;
